import { useCallback, useMemo } from 'react'
import noop from 'lib/function/noop'
import { useChatList } from 'tripPlanner/hooks/api/chat'
import useClassicTripHistory from 'tripPlanner/legacyArchive/useClassicTripHistory'
import { buildRecentChatItems, type RecentChatItem } from './recentChatItems'

interface RecentChatItems {
  items: Array<RecentChatItem>
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useRecentChatItems({ isLoggedIn }: { isLoggedIn: boolean }): RecentChatItems {
  const chatList = useChatList({ enabled: isLoggedIn, staleTime: 30_000 })
  const classicTrips = useClassicTripHistory({ enabled: isLoggedIn })
  const { refetch: refetchChats } = chatList
  const { refetch: refetchClassicTrips } = classicTrips

  const items = useMemo(
    () => buildRecentChatItems(chatList.data ?? [], classicTrips.data),
    [chatList.data, classicTrips.data],
  )

  const refetch = useCallback(() => {
    refetchChats().catch(noop)
    refetchClassicTrips().catch(noop)
  }, [refetchChats, refetchClassicTrips])

  return {
    items,
    isLoading: isLoggedIn && (chatList.isInitialLoading || classicTrips.isLoading),
    isError: chatList.isError || classicTrips.isError,
    refetch,
  }
}
